import { Component, OnInit, OnDestroy, ChangeDetectorRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  FormArray,
  FormControl,
  Validators,
} from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { SharedModule } from "@shared/shared.module";
import { SharedService } from "../services/shared.service";
import { fps_master_list, url } from "../global/constant";
import { VideoxService } from "../services/videox.service";
import { MasterService } from "../services/master.service";
import { finalize, catchError, of, Subscription } from "rxjs";
import { VideoPlayerComponent } from "@shared/common-components/video-player/video-player.component";
import { ConfirmService } from "../services/confirm.service";
import { CreditService } from "../services/credit.service";

@Component({
  selector: "app-video-x",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
    VideoPlayerComponent,
  ],
  templateUrl: "./video-x.component.html",
  styleUrl: "./video-x.component.scss",
})
export class VideoXComponent implements OnInit, OnDestroy {
  // Route parameters
  videoId: string | number | null = null;
  videoXId: string | number | null = null;
  campaignId: string | number | null = null;
  // Frame selection
  selectedFrameId: number | null = null;
  selectedSceneId: number | null = null;
  selectedShotId: number | null = null;
  expandedSceneId: number | null = null;

  // Scenes with shots grouped
  scenes: {
    id: number;
    scene_number: number;
    scene_title: string;
    shots: {
      id: number;
      shot_number: string;
      image: string;
      description: string;
      sceneId: number;
      shotId: number;
      shotData: any;
    }[];
  }[] = [];

  // Flat frames array (for backward compatibility)
  frames: any[] = [];

  // Reactive Form
  videoGenerationForm!: FormGroup;
  isFormSubmitted: boolean = false;

  // Panel state
  isLeftPanelCollapsed: boolean = false;
  isRightPanelVisible: boolean = false;

  // Camera angle options
  cameraAngleOptions = [];

  // FPS options
  fpsOptions = fps_master_list;

  // Camera movement options
  cameraMovementOptions = [];

  // Model options (populated from API)
  modelOptions: any[] = [];

  // Duration options (up to 60 seconds)
  durationOptions = Array.from({ length: 60 }, (_, i) => ({
    id: (i + 1).toString(),
    name: `${i + 1} sec`,
  }));

  videoXDetails: any = null;

  // Video player
  aivideos: any[] = [];
  currentVideoUrl: string | null = null;
  selectedVideoId: number | null = null;

  // Credits
  credits: number = 0;
  private creditSubscription?: Subscription;

  // Generation details (can be populated from API or form)
  generationDetails: {
    duration: string;
    fps: string;
    model: string;
  } | null = null;

  currentView: "home" | "player" = "home";
  videoGallaryList: any[] = [];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    public sharedService: SharedService,
    private videoXService: VideoxService,
    private confirmService: ConfirmService,
    private masterService: MasterService,
    private creditService: CreditService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const queryParams = this.route.snapshot.queryParams;
    this.videoId = queryParams["videoId"] || null;
    this.videoXId = queryParams["videoXId"] || null;
    this.initVideoGenerationForm();
    this.loadModelOptions();
    this.loadCameraTypes();

    if (this.videoXId) {
      this.loadVideoXDetails();
    } else if (this.videoId) {
      // If creating new VideoX, try to load shotlist to get frames
      this.loadShotlistForFrames();
    }

    // Subscribe to credit count updates
    this.creditSubscription = this.creditService.$creditCount.subscribe(
      (count) => {
        this.credits = count;
        this.cdr.detectChanges();
      }
    );

    // Note: refreshCredits() is automatically called by CreditService when companyId changes
    // No need to call it manually here
  }

  ngOnDestroy(): void {
    // Unsubscribe from credit updates
    if (this.creditSubscription) {
      this.creditSubscription.unsubscribe();
    }
  }

  initVideoGenerationForm(): void {
    this.videoGenerationForm = this.fb.group({
      actionPrompt: ["", Validators.required],
      userInstruction: [""],
      dialogueLines: this.fb.array([
        this.fb.group({
          character: [""],
          line: [""],
        }),
      ]),
      cameraAngle: ["", Validators.required],
      fps: ["24", Validators.required],
      cameraMovement: ["", Validators.required],
      model: ["", Validators.required],
      duration: ["5", Validators.required],
    });
  }

  loadModelOptions(): void {
    const operationType = "Create";
    const payload = {
      tool: "VideoX",
      operation: operationType,
    };

    this.masterService
      .getAIModelList(payload)
      .pipe(catchError((err) => of(null)))
      .subscribe((response: any) => {
        if (response?.statusCode === 200) {
          this.modelOptions = response?.data?.creditstools || [];

          // Set default model if available and form doesn't have a value
          if (
            this.modelOptions.length > 0 &&
            !this.videoGenerationForm.get("model")?.value
          ) {
            this.videoGenerationForm.patchValue({
              model: this.modelOptions[0].model_id,
            });
          }
        }
      });
  }

  loadCameraTypes(): void {
    this.masterService
      .getSetupsList()
      .pipe(catchError((err) => of(null)))
      .subscribe((response: any) => {
        if (response?.statusCode === 200) {
          this.cameraAngleOptions =
            response?.data?.filter(
              (x: any) => x.camera_type === "Camera Angle"
            ) || [];

          this.cameraMovementOptions = response?.data?.filter(
            (x: any) => x.camera_type === "Camera Movement"
          );
        }
      });
  }

  loadVideoXDetails(): void {
    this.sharedService.showSpinner();
    this.videoXService
      .fetchVideoXDetails(this.videoXId)
      .pipe(finalize(() => this.sharedService.hideSpinner()))
      .subscribe((res: any) => {
        if (res && res.statusCode == 200) {
          this.videoXDetails = res.data;
          this.videoGallaryList = res?.data?.shotlist?.scenes || [];
          // Set campaign ID from video data
          if (res.data?.video?.campaign_id) {
            this.campaignId = res.data.video.campaign_id;
          }

          // Map shotlist scenes/shots to frames
          this.mapShotlistToFrames(res.data);

          // Populate form with existing data if available
          this.populateFormFromData(res.data);
        }
      });
  }

  mapShotlistToFrames(data: any): void {
    if (!data?.shotlist?.scenes || data.shotlist.scenes.length === 0) {
      return;
    }

    const scenesArray: any[] = [];
    const framesArray: any[] = [];
    let frameIndex = 1;

    // Sort scenes by scene_number to ensure correct order
    const sortedScenes = [...data.shotlist.scenes].sort(
      (a: any, b: any) => a.scene_number - b.scene_number
    );

    sortedScenes.forEach((scene: any) => {
      if (scene.shots && scene.shots.length > 0) {
        // Sort shots by shot_number (handle formats like "1A", "2A", etc.)
        const sortedShots = [...scene.shots].sort((a: any, b: any) => {
          // Extract numeric part and letter part
          const aMatch = a.shot_number.match(/(\d+)([A-Za-z]*)/);
          const bMatch = b.shot_number.match(/(\d+)([A-Za-z]*)/);

          if (!aMatch || !bMatch) return 0;

          const aNum = parseInt(aMatch[1]) || 0;
          const bNum = parseInt(bMatch[1]) || 0;

          if (aNum !== bNum) {
            return aNum - bNum;
          }

          // If numbers are same, compare letters
          const aLetter = aMatch[2] || "";
          const bLetter = bMatch[2] || "";
          return aLetter.localeCompare(bLetter);
        });

        const shotsArray: any[] = [];

        sortedShots.forEach((shot: any) => {
          // Get storyboard image if available, otherwise use placeholder
          let imageUrl = "../../assets/images/storyboard_default_image.png";
          if (
            shot.story_images &&
            shot.story_images.length > 0 &&
            shot.story_images[0].image
          ) {
            imageUrl = shot.story_images[0].image;
          }

          const shotData = {
            id: frameIndex,
            shot_number: shot.shot_number,
            image: imageUrl,
            description: shot.description,
            sceneId: scene.id,
            shotId: shot.id,
            shotData: shot,
          };

          shotsArray.push(shotData);
          framesArray.push({
            ...shotData,
            scene: scene.scene_number,
            sceneTitle: scene.scene_title,
            sceneData: scene,
          });

          frameIndex++;
        });

        scenesArray.push({
          id: scene.id,
          scene_number: scene.scene_number,
          scene_title: scene.scene_title,
          shots: shotsArray,
        });
      }
    });

    // Update scenes and frames arrays
    if (scenesArray.length > 0) {
      this.scenes = scenesArray;
      this.frames = framesArray;

      // Select first shot of first scene by default
      if (scenesArray[0].shots.length > 0) {
        const firstShot = scenesArray[0].shots[0];
        this.selectShot(scenesArray[0].id, firstShot.shotId, firstShot.id);
      }
    } else {
      // Reset videos if no scenes
      this.aivideos = [];
      this.currentVideoUrl = null;
      this.selectedVideoId = null;
      this.generationDetails = null;
      this.isRightPanelVisible = false;
    }
  }

  populateFormFromData(data: any): void {
    // If there's existing form data in the API response, populate it
    // For now, we'll keep default values unless API provides specific fields
    // You can extend this based on your API structure
    // Example: If API returns actionPrompt, cameraAngle, etc.
    // if (data.actionPrompt) {
    //   this.videoGenerationForm.patchValue({
    //     actionPrompt: data.actionPrompt
    //   });
    // }
  }

  loadShotlistForFrames(): void {
    // When creating new VideoX, we might need to fetch shotlist separately
    // For now, frames will be loaded when VideoX details are fetched
    // If you have a shotlistId in video details, you can fetch it here
  }

  get dialogueLinesFormArray(): FormArray {
    return this.videoGenerationForm.get("dialogueLines") as FormArray;
  }

  get dialogueLineFormGroups(): FormGroup[] {
    return this.dialogueLinesFormArray.controls as FormGroup[];
  }

  // Getter methods for form controls
  getControl(controlName) {
    return this.videoGenerationForm.controls[controlName];
  }
  // get actionPromptControl(): FormControl {
  //   return this.videoGenerationForm.get("actionPrompt") as FormControl;
  // }

  // get cameraAngleControl(): FormControl {
  //   return this.videoGenerationForm.get("cameraAngle") as FormControl;
  // }

  // get fpsControl(): FormControl {
  //   return this.videoGenerationForm.get("fps") as FormControl;
  // }

  // get cameraMovementControl(): FormControl {
  //   return this.videoGenerationForm.get("cameraMovement") as FormControl;
  // }

  getDialogueCharacterControl(index: number): FormControl {
    const dialogueGroup = this.dialogueLinesFormArray.at(index) as FormGroup;
    return dialogueGroup.get("character") as FormControl;
  }

  getDialogueLineControl(index: number): FormControl {
    const dialogueGroup = this.dialogueLinesFormArray.at(index) as FormGroup;
    return dialogueGroup.get("line") as FormControl;
  }

  // Getter methods to access form values
  get actionPromptValue(): string {
    return this.videoGenerationForm.get("actionPrompt")?.value || "";
  }

  get cameraAngleValue(): string {
    return this.videoGenerationForm.get("cameraAngle")?.value || "";
  }

  get fpsValue(): string {
    return this.videoGenerationForm.get("fps")?.value || "";
  }

  get cameraMovementValue(): string {
    return this.videoGenerationForm.get("cameraMovement")?.value || "";
  }

  get dialogueLinesValue(): { character: string; line: string }[] {
    return this.videoGenerationForm.get("dialogueLines")?.value || [];
  }

  toggleScene(sceneId: number): void {
    if (this.expandedSceneId === sceneId) {
      this.expandedSceneId = null;
    } else {
      this.expandedSceneId = sceneId;
    }
  }

  selectShot(sceneId: number, shotId: number, frameId: number): void {
    this.selectedSceneId = sceneId;
    this.selectedShotId = shotId;
    this.selectedFrameId = frameId;
    
    // Expand the scene so the selected shot is visible in the left panel
    this.expandedSceneId = sceneId;

    // Find the selected shot and populate Action Prompt with its description
    for (const scene of this.scenes) {
      if (scene.id === sceneId) {
        const shot = scene.shots.find((s) => s.shotId === shotId);
        if (shot) {
          // Populate Action Prompt with shot description
          if (shot.description) {
            this.videoGenerationForm.patchValue({
              actionPrompt: shot.description,
            });
          }

          // Populate Dialogue Lines from shot's dialogue array
          if (
            shot.shotData &&
            shot.shotData.dialogue &&
            Array.isArray(shot.shotData.dialogue) &&
            shot.shotData.dialogue.length > 0
          ) {
            // Clear existing dialogue lines
            while (this.dialogueLinesFormArray.length > 0) {
              this.dialogueLinesFormArray.removeAt(0);
            }

            // Add dialogue lines from shot data
            shot.shotData.dialogue.forEach((dialogueItem: any) => {
              const dialogueLineGroup = this.fb.group({
                character: [dialogueItem.name || "", Validators.required],
                line: [dialogueItem.dialogue || "", Validators.required],
              });
              this.dialogueLinesFormArray.push(dialogueLineGroup);
            });
          } else {
            // If no dialogue exists, ensure at least one empty dialogue line
            // if (this.dialogueLinesFormArray.length === 0) {
            // Clear existing dialogue lines
            while (this.dialogueLinesFormArray.length > 0) {
              this.dialogueLinesFormArray.removeAt(0);
            }
            this.addDialogueLine();
            // }
          }

          // Load aivideos array from shot data
          if (
            shot.shotData &&
            shot.shotData.aivideos &&
            Array.isArray(shot.shotData.aivideos)
          ) {
            this.aivideos = shot.shotData.aivideos;
            // Select approved video if exists, otherwise select first video
            if (this.aivideos.length > 0) {
              const approvedVideo = this.aivideos.find((v) => v.is_approved);
              const videoToSelect = approvedVideo || this.aivideos[0];
              this.selectVideo(videoToSelect.id);
            } else {
              this.currentVideoUrl = null;
              this.selectedVideoId = null;
              this.generationDetails = null;
              this.isRightPanelVisible = false;
            }
          } else {
            this.aivideos = [];
            this.currentVideoUrl = null;
            this.selectedVideoId = null;
            this.generationDetails = null;
            this.isRightPanelVisible = false;
          }
        }
        break;
      }
    }
  }

  getSelectedFrame() {
    if (!this.selectedSceneId || !this.selectedShotId) {
      // Return default frame if nothing selected
      if (this.scenes.length > 0 && this.scenes[0].shots.length > 0) {
        const firstShot = this.scenes[0].shots[0];
        return {
          id: firstShot.id,
          scene: this.scenes[0].scene_number,
          shot: firstShot.shot_number,
          image: firstShot.image,
          sceneId: this.scenes[0].id,
          shotId: firstShot.shotId,
          description: firstShot.description,
          sceneTitle: this.scenes[0].scene_title,
        };
      }
      return {
        id: 0,
        scene: 0,
        shot: "",
        image: "../../assets/images/storyboard_default_image.png",
        sceneId: null,
        shotId: null,
        description: "",
        sceneTitle: "",
      };
    }

    // Find selected shot from scenes
    for (const scene of this.scenes) {
      if (scene.id === this.selectedSceneId) {
        const shot = scene.shots.find((s) => s.shotId === this.selectedShotId);
        if (shot) {
          return {
            id: shot.id,
            scene: scene.scene_number,
            shot: shot.shot_number,
            image: shot.image,
            sceneId: scene.id,
            shotId: shot.shotId,
            description: shot.description,
            sceneTitle: scene.scene_title,
          };
        }
      }
    }

    // Fallback
    return {
      id: 0,
      scene: 0,
      shot: "",
      image: "../../assets/images/storyboard_default_image.png",
      sceneId: null,
      shotId: null,
      description: "",
      sceneTitle: "",
    };
  }

  isSceneExpanded(sceneId: number): boolean {
    return this.expandedSceneId === sceneId;
  }

  isShotSelected(sceneId: number, shotId: number): boolean {
    return this.selectedSceneId === sceneId && this.selectedShotId === shotId;
  }

  addDialogueLine(): void {
    const dialogueLineGroup = this.fb.group({
      character: ["", Validators.required],
      line: ["", Validators.required],
    });
    this.dialogueLinesFormArray.push(dialogueLineGroup);
  }

  removeDialogueLine(index: number): void {
    if (this.dialogueLinesFormArray.length > 1) {
      this.dialogueLinesFormArray.removeAt(index);
    }
  }

  generateVideo(): void {
    this.isFormSubmitted = true;
    if (this.videoGenerationForm.valid) {
      const formValue = this.videoGenerationForm.value;

      // Get selected shot with full data
      const selectedShot = this.getSelectedShotWithData();
      if (!selectedShot || !selectedShot.shotId) {
        this.sharedService.errorToastNotification("Please select a shot first");
        return;
      }

      // Get full camera_angle object
      const cameraAngleValue = formValue.cameraAngle;
      const cameraAngleObj = this.cameraAngleOptions.find(
        (option: any) =>
          option.id === cameraAngleValue || option.name === cameraAngleValue
      );

      // Get full camera_move object
      const cameraMoveValue = formValue.cameraMovement;
      const cameraMoveObj = this.cameraMovementOptions.find(
        (option: any) =>
          option.id === cameraMoveValue || option.name === cameraMoveValue
      );

      // Format dialogue_prompt from dialogueLines FormArray
      const dialoguePrompt = formValue.dialogueLines.map((dialogue: any) => ({
        name: dialogue.character,
        dialogue: dialogue.line,
      }));

      // Get startimage_url from shot's story_images
      let startimageUrl = null;
      if (
        selectedShot.shotData &&
        selectedShot.shotData.story_images &&
        selectedShot.shotData.story_images.length > 0 &&
        selectedShot.shotData.story_images[0].image
      ) {
        startimageUrl = selectedShot.shotData.story_images[0].image;
      }

      // Build payload
      const payload = {
        videox_id: this.videoXId || null,
        shot_id: selectedShot.shotId,
        action_prompt: formValue.actionPrompt,
        dialogue_prompt: dialoguePrompt,
        camera_angle: cameraAngleObj || null,
        camera_move: cameraMoveObj || null,
        fps: formValue.fps,
        duration: formValue.duration,
        model_id: formValue.model,
        startimage_url: startimageUrl,
        endimage_url: null,
      };

      // Call the service to generate video
      this.sharedService.showSpinner();
      this.videoXService
        .onVideoXGenerate(payload)
        .pipe(finalize(() => this.sharedService.hideSpinner()))
        .subscribe({
          next: (response: any) => {
            if (response && response.statusCode === 200) {
              this.sharedService.successToastNotification(
                response?.message || "Video generation started successfully"
              );
              // Handle success response (e.g., update UI with generated video)
              console.log("Video generation response:", response);
            } else {
              this.sharedService.errorToastNotification(
                response?.message || "Failed to generate video"
              );
            }
          },
          error: (error: any) => {
            console.error("Error generating video:", error);
            this.sharedService.errorToastNotification(
              error?.error?.message ||
                "An error occurred while generating video"
            );
          },
        });
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.videoGenerationForm.controls).forEach((key) => {
        const control = this.videoGenerationForm.get(key);
        control?.markAsTouched();
      });
      // Mark FormArray controls as touched
      this.dialogueLinesFormArray.controls.forEach((control) => {
        if (control instanceof FormGroup) {
          Object.keys(control.controls).forEach((key) => {
            control.get(key)?.markAsTouched();
          });
        }
      });
    }
  }

  // Helper method to get selected shot with full data
  getSelectedShotWithData(): any {
    if (!this.selectedSceneId || !this.selectedShotId) {
      return null;
    }

    for (const scene of this.scenes) {
      if (scene.id === this.selectedSceneId) {
        const shot = scene.shots.find((s) => s.shotId === this.selectedShotId);
        return shot || null;
      }
    }

    return null;
  }

  checkAnyVideoIsApproved() {
    if (this.aivideos.filter((v) => v.is_approved).length) {
      this.confirmService
        .confirm(
          "Another video is already approved. Replace it with this one?",
          ""
        )
        .subscribe((result) => {
          if (result) {
            this.approveVideo(true);
          }
        });
    } else {
      this.approveVideo(true);
    }
  }

  approveVideo(isApproved: boolean): void {
    const payload = {
      video_id: this.selectedVideoId,
      is_approved: isApproved,
    };
    this.sharedService.showSpinner();

    this.videoXService
      .onShotVideoApprove(payload)
      .pipe(finalize(() => this.sharedService.hideSpinner()))
      .subscribe((response: any) => {
        if (response && response.statusCode === 200) {
          this.sharedService.successToastNotification(
            response?.message || "Video approved successfully"
          );
          this.aivideos.map((x) => {
            if (x.id === this.selectedVideoId) {
              x.is_approved = isApproved;
            }
          });
        } else {
          this.sharedService.errorToastNotification(
            response?.message || "Failed to generate video"
          );
        }
      });
  }

  exportMP4(): void {
    // Implement export logic
    console.log("Exporting MP4...");
  }

  saveToFrameX(): void {
    // Implement save to FrameX logic
    console.log("Saving to FrameX...");
  }

  downloadMP4(): void {
    if (!this.currentVideoUrl || !this.selectedVideoId) {
      this.sharedService.errorToastNotification("No video selected for download");
      return;
    }

    // Create a temporary anchor element to trigger download
    const link = document.createElement("a");
    link.href = this.currentVideoUrl;
    link.download = `video-${this.selectedVideoId}.mp4`;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  shareToReview(): void {
    // TODO: Implement share to review functionality
    console.log("Share to Review - To be implemented");
  }

  deleteVideo(): void {
    if (!this.selectedVideoId) {
      this.sharedService.errorToastNotification("No video selected for deletion");
      return;
    }

    // Show confirmation dialog
    this.confirmService
      .confirm("Are you sure you want to delete this video?", "")
      .subscribe((result) => {
        if (result) {
          const payload = {
            aivideo_id: this.selectedVideoId,
          };

          this.sharedService.showSpinner();
          this.videoXService
            .removeVideo(payload)
            .pipe(finalize(() => this.sharedService.hideSpinner()))
            .subscribe({
              next: (response: any) => {
                if (response && response.statusCode === 200) {
                  this.sharedService.successToastNotification(
                    response?.message || "Video deleted successfully"
                  );
                  
                  // Remove video from aivideos array
                  this.aivideos = this.aivideos.filter(
                    (v) => v.id !== this.selectedVideoId
                  );

                  // Select another video if available, otherwise clear selection
                  if (this.aivideos.length > 0) {
                    const approvedVideo = this.aivideos.find((v) => v.is_approved);
                    const videoToSelect = approvedVideo || this.aivideos[0];
                    this.selectVideo(videoToSelect.id);
                  } else {
                    this.currentVideoUrl = null;
                    this.selectedVideoId = null;
                    this.generationDetails = null;
                    this.isRightPanelVisible = false;
                  }

                  // Update the shot data in scenes to reflect the deletion
                  this.updateShotVideosAfterDeletion();
                } else {
                  this.sharedService.errorToastNotification(
                    response?.message || "Failed to delete video"
                  );
                }
              },
              error: (error: any) => {
                console.error("Error deleting video:", error);
                this.sharedService.errorToastNotification(
                  error?.error?.message ||
                    "An error occurred while deleting video"
                );
              },
            });
        }
      });
  }

  updateShotVideosAfterDeletion(): void {
    // Update the shot data in scenes to reflect the deletion
    if (this.selectedSceneId && this.selectedShotId) {
      for (const scene of this.scenes) {
        if (scene.id === this.selectedSceneId) {
          const shot = scene.shots.find((s) => s.shotId === this.selectedShotId);
          if (shot && shot.shotData) {
            shot.shotData.aivideos = this.aivideos;
          }
          break;
        }
      }
    }

    // Also update videoGallaryList
    for (const scene of this.videoGallaryList) {
      const shot = scene.shots?.find((s: any) => s.shotId === this.selectedShotId);
      if (shot) {
        shot.aivideos = this.aivideos;
        break;
      }
    }
  }

  selectVideo(videoId: number): void {
    const video = this.aivideos.find((v) => v.id === videoId);
    if (video && video.url) {
      this.selectedVideoId = videoId;
      this.currentVideoUrl = video.url;
      this.isRightPanelVisible = true;

      // Populate generation details from selected video
      const model = video.model_id
        ? this.modelOptions.find((m) => m.model_id === video.model_id)
        : null;

      this.generationDetails = {
        duration: video.duration ? `${video.duration}s` : "N/A",
        fps: video.fps ? video.fps.toString() : "N/A",
        model: model ? model.model || "Unknown" : "N/A",
      };
    } else {
      // Reset generation details if no video selected
      this.generationDetails = null;
      this.isRightPanelVisible = false;
    }
  }

  getVideoThumbnail(video: any): string {
    // You can implement thumbnail generation logic here
    // For now, return a placeholder or use the first frame
    return (
      video.thumbnail || "../../assets/images/storyboard_default_image.png"
    );
  }

  formatDuration(duration: string | number): string {
    const seconds =
      typeof duration === "string" ? parseInt(duration) : duration;
    if (!seconds || isNaN(seconds)) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }

  goBack(): void {
    if (!this.videoXId) {
      history.back();
    } else {
      this.sharedService.gotopage(
        url.videoDetails + `?id=${this.campaignId}&videoId=${this.videoId}`
      );
    }
  }

  toggleLeftPanel(): void {
    this.isLeftPanelCollapsed = !this.isLeftPanelCollapsed;
  }

  switchView(shot: any, view: "home" | "player"): void {
    if (view === "player") {
      // If shot has videos, collapse left panel and load the shot
      if (shot?.aivideos?.length) {
        this.isLeftPanelCollapsed = true;
        // Find the shot in scenes and select it to load videos
        this.loadShotForPlayer(shot);
      } else {
        // If shot has no videos, keep left panel open for generation
        this.isLeftPanelCollapsed = false;
        // Still need to select the shot to populate form
        this.loadShotForPlayer(shot);
      }
    } else {
      // When going back to home view
      this.isLeftPanelCollapsed = false;
    }
    this.currentView = view;
  }

  loadShotForPlayer(shot: any): void {
    // Find the shot in scenes array (used by left panel) by matching shotId
    // The shot from videoGallaryList can have either id or shotId property
    // In scenes, shotId is mapped from shot.id, so we match shotId with shot.id or shot.shotId
    const shotIdToMatch = shot.shotId || shot.id;
    for (const scene of this.scenes) {
      const foundShot = scene.shots?.find((s: any) => s.shotId === shotIdToMatch);
      if (foundShot) {
        // Select the shot which will load videos and populate form
        this.selectShot(scene.id, foundShot.shotId, foundShot.id);
        break;
      }
    }
  }

  onGenerateVideoClick(shot: any, event: Event): void {
    // Prevent event propagation to avoid triggering the shot-card click
    event.stopPropagation();
    
    // Switch to player view with left panel open for generation
    this.switchView(shot, 'player');
  }
}
